import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { Plus, UserPlus, Key, Mail, Shield, Trash2, Eye, EyeOff, Edit } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'employee',
    employee_id: ''
  });

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'hr_manager') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [usersRes, empRes] = await Promise.all([
        apiClient.get('/api/users'),
        apiClient.get('/api/employees')
      ]);
      setUsers(usersRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      await apiClient.post('/api/auth/register', newUser);
      alert('User account created successfully!');
      fetchData();
      setShowCreateModal(false);
      setNewUser({
        email: '',
        password: '',
        full_name: '',
        role: 'employee',
        employee_id: ''
      });
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.emp_code} - ${emp.full_name}` : empId;
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user account for ${userName}?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/users/${userId}`);
      alert('User account deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      id: user.id,
      full_name: user.full_name,
      email: user.email,
      role: user.role,
      is_active: user.is_active
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      await apiClient.put(`/api/users/${editingUser.id}`, {
        full_name: editingUser.full_name,
        role: editingUser.role,
        is_active: editingUser.is_active
      });
      alert('User updated successfully!');
      fetchData();
      setShowEditModal(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleReactivateUser = async (userId, userName) => {
    if (!window.confirm(`Reactivate user account for ${userName}?`)) {
      return;
    }

    try {
      await apiClient.put(`/api/users/${userId}`, {
        is_active: true
      });
      alert('User account reactivated successfully');
      fetchData();
    } catch (error) {
      console.error('Error reactivating user:', error);
      alert('Error reactivating user');
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-[#3B1E21] text-[#FFB3B8] border border-[#5C2A2E]',
      hr_manager: 'bg-[rgba(14,165,233,0.15)] text-[#0EA5E9] border border-[rgba(14,165,233,0.3)]',
      dept_manager: 'bg-[#2A2633] text-[#E8D3FF] border border-[#4B3D66]',
      employee: 'bg-[#073B3B] text-[#4DD9D9] border border-[#0E5F5F]'
    };
    return colors[role] || 'bg-[hsl(215_8%_20%)] text-[hsl(210_15%_92%)]';
  };

  if (user?.role !== 'admin' && user?.role !== 'hr_manager') {
    return (
      <Layout>
        <div className='text-center py-20'>
          <Shield size={64} className='mx-auto mb-4 text-[hsl(215_10%_70%)] opacity-50' />
          <p className='text-[hsl(215_10%_70%)]'>Access Denied. Only Admin and HR Manager can access User Management.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-full'>
          <div className='loading-spinner'></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='page-header flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-[hsl(210_15%_92%)]'>User Management</h1>
          <p className='text-[hsl(215_10%_70%)] mt-1'>Manage user accounts and login credentials - {users.filter(u => u.is_active).length} active users</p>
        </div>
        <div className='flex gap-3'>
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors duration-150 ${showInactive ? 'bg-[hsl(215_8%_20%)] border-[#00B8B8]/50' : 'bg-[hsl(220_6%_8%)] border-[hsl(215_10%_22%)]'} text-[hsl(210_15%_92%)]`}
          >
            {showInactive ? 'Hide Inactive' : 'Show Inactive'}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            data-testid='create-user-button'
            className='flex items-center gap-2 bg-[#00B8B8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0ED2D2] transition-colors duration-150'
          >
            <UserPlus size={20} />
            Create User Login
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className='bg-[hsl(220_6%_8%)] rounded-xl border border-[hsl(215_10%_22%)]'>
        <div className='p-6 border-b border-[hsl(215_10%_22%)]'>
          <h3 className='text-lg font-semibold text-[hsl(210_15%_92%)]'>User Accounts</h3>
        </div>
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead className='bg-[hsl(220_6%_12%)]'>
              <tr>
                <th className='text-left p-4 font-medium text-[hsl(210_15%_92%)]'>Name</th>
                <th className='text-left p-4 font-medium text-[hsl(210_15%_92%)]'>Email</th>
                <th className='text-left p-4 font-medium text-[hsl(210_15%_92%)]'>Role</th>
                <th className='text-left p-4 font-medium text-[hsl(210_15%_92%)]'>Employee</th>
                <th className='text-left p-4 font-medium text-[hsl(210_15%_92%)]'>Status</th>
                <th className='text-left p-4 font-medium text-[hsl(210_15%_92%)]'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan='6' className='text-center p-8 text-[hsl(215_10%_70%)]'>No users found</td>
                </tr>
              ) : (
                users.filter(u => showInactive ? true : u.is_active).map((u) => (
                  <tr key={u.id} className='border-t border-[hsl(215_10%_22%)] hover:bg-[hsl(215_8%_20%)/50] transition-colors duration-150'>
                    <td className='p-4 font-medium text-[hsl(210_15%_92%)]'>{u.full_name}</td>
                    <td className='p-4 text-[hsl(215_10%_70%)]'>{u.email}</td>
                    <td className='p-4'>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadge(u.role)}`}>
                        {u.role.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className='p-4 text-[hsl(215_10%_70%)]'>{u.employee_id ? getEmployeeName(u.employee_id) : 'N/A'}</td>
                    <td className='p-4'>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${u.is_active ? 'bg-[#073B3B] text-[#4DD9D9] border border-[#0E5F5F]' : 'bg-[#3B1E21] text-[#FFB3B8] border border-[#5C2A2E]'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className='p-4'>
                      <div className='flex items-center gap-2'>
                        {u.is_active ? (
                          <>
                            <button
                              onClick={() => handleEditUser(u)}
                              className='p-2 text-[#0EA5E9] hover:bg-[rgba(14,165,233,0.1)] rounded transition-colors duration-150'
                              title='Edit User'
                            >
                              <Edit size={16} />
                            </button>
                            {u.role !== 'admin' && (
                              <button
                                onClick={() => handleDeleteUser(u.id, u.full_name)}
                                data-testid={`delete-user-${u.id}`}
                                className='p-2 text-[#EF4444] hover:bg-[rgba(239,68,68,0.1)] rounded transition-colors duration-150'
                                title='Delete User'
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={() => handleReactivateUser(u.id, u.full_name)}
                            className='px-3 py-1 bg-[#10B981] text-white rounded text-xs hover:bg-[#059669] transition-colors duration-150'
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className='fixed inset-0 bg-black/75 flex items-center justify-center z-50' onClick={() => setShowCreateModal(false)}>
          <div className='bg-[hsl(220_6%_8%)] border border-[hsl(215_10%_22%)] rounded-xl p-6 max-w-2xl w-full m-4 shadow-[0_12px_40px_rgba(0,0,0,0.50)]' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[hsl(210_15%_92%)] mb-6'>Create Employee Login</h2>
            
            <form onSubmit={handleCreateUser} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Select Employee*</label>
                <select
                  required
                  value={newUser.employee_id}
                  onChange={(e) => {
                    const emp = employees.find(em => em.id === e.target.value);
                    setNewUser({
                      ...newUser,
                      employee_id: e.target.value,
                      full_name: emp?.full_name || '',
                      email: emp?.email || ''
                    });
                  }}
                  className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                >
                  <option value=''>Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.emp_code} - {emp.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Full Name*</label>
                  <input
                    type='text'
                    required
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                    readOnly
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Email*</label>
                  <input
                    type='email'
                    required
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Password*</label>
                <div className='relative'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                    placeholder='Create a strong password'
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-3 top-1/2 transform -translate-y-1/2 text-[hsl(215_10%_70%)]'
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Role*</label>
                <select
                  required
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                >
                  <option value='employee'>Employee (Self-service access)</option>
                  {user?.role === 'admin' && (
                    <>
                      <option value='dept_manager'>Department Manager</option>
                      <option value='hr_manager'>HR Manager</option>
                      <option value='admin'>Admin (Full Access)</option>
                    </>
                  )}
                </select>
                <p className='text-xs text-[hsl(215_10%_70%)] mt-1'>
                  {user?.role === 'hr_manager' ? 'HR Manager can only create Employee logins' : 'Admin can create any role'}
                </p>
              </div>

              <div className='bg-[rgba(14,165,233,0.1)] border border-[rgba(14,165,233,0.3)] rounded-lg p-4'>
                <p className='text-sm text-[#0EA5E9]'>
                  <strong>Note:</strong> This will create a login account for the selected employee. 
                  They will be able to login with the email and password you set here.
                </p>
              </div>

              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => setShowCreateModal(false)}
                  className='px-6 py-2 border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] hover:bg-[hsl(215_8%_20%)] transition-colors duration-150'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  data-testid='submit-create-user'
                  className='px-6 py-2 bg-[#00B8B8] text-white rounded-lg hover:bg-[#0ED2D2] transition-colors duration-150'
                >
                  Create Login
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className='fixed inset-0 bg-black/75 flex items-center justify-center z-50' onClick={() => setShowEditModal(false)}>
          <div className='bg-[hsl(220_6%_8%)] border border-[hsl(215_10%_22%)] rounded-xl p-6 max-w-md w-full m-4 shadow-[0_12px_40px_rgba(0,0,0,0.50)]' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[hsl(210_15%_92%)] mb-6'>Edit User Account</h2>
            
            <form onSubmit={handleUpdateUser} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Full Name*</label>
                <input
                  type='text'
                  required
                  value={editingUser.full_name}
                  onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                  className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Email</label>
                <input
                  type='email'
                  value={editingUser.email}
                  disabled
                  className='w-full px-4 py-2 bg-[hsl(215_8%_20%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(215_10%_70%)] cursor-not-allowed'
                />
                <p className='text-xs text-[hsl(215_10%_70%)] mt-1'>Email cannot be changed</p>
              </div>

              <div>
                <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Role*</label>
                <select
                  required
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                  className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                >
                  <option value='employee'>Employee</option>
                  {user?.role === 'admin' && (
                    <>
                      <option value='dept_manager'>Department Manager</option>
                      <option value='hr_manager'>HR Manager</option>
                      <option value='admin'>Admin</option>
                    </>
                  )}
                </select>
              </div>

              <div className='flex items-center gap-2 p-4 bg-[hsl(215_8%_20%)] rounded-lg'>
                <input
                  type='checkbox'
                  checked={editingUser.is_active}
                  onChange={(e) => setEditingUser({...editingUser, is_active: e.target.checked})}
                  className='w-4 h-4 accent-[#00B8B8]'
                />
                <label className='text-sm text-[hsl(210_15%_92%)]'>Account Active</label>
              </div>

              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  className='px-6 py-2 border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] hover:bg-[hsl(215_8%_20%)] transition-colors duration-150'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='px-6 py-2 bg-[#00B8B8] text-white rounded-lg hover:bg-[#0ED2D2] transition-colors duration-150'
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UserManagement;